import React, { useState, useEffect, useCallback } from 'react';
import { 
  Layout, 
  Button, 
  Typography, 
  Space, 
  Modal, 
  Breadcrumb, 
  Card
} from 'antd';
import { 
  FileTextOutlined, 
  PlusOutlined, 
  HomeOutlined,
  EditOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';

// Import our components
import TemplateList from '../components/template/TemplateList';
import TemplateEditor from '../components/template/TemplateEditor';
import TemplatePrintPreview from '../components/template/TemplatePrintPreview';

const { Content } = Layout;
const { Title } = Typography;

// Main Template Management Page
const TemplateManagementPage = () => {
  const [activeView, setActiveView] = useState('list');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  
  // Get user permissions from Redux store
  const user = useSelector(state => state.user);
  const userPermissions = {
    isManager: user?.IsManager,
  };
  
  // Set document title
  useEffect(() => {
    document.title = 'Template Management';
  }, []);
  
  // Handle unsaved changes
  const handleChangesDetected = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);
  
  const handleChangesSaved = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);
  
  // Handle before unload (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  // Handle view changes with unsaved changes prevention
  const handleChangeView = (view, templateId = null) => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to leave?',
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => {
          setHasUnsavedChanges(false);
          changeView(view, templateId);
        }
      });
    } else {
      changeView(view, templateId);
    }
  };
  
  // View change helper
  const changeView = (view, templateId = null) => {
    setActiveView(view);
    
    if (templateId !== null) {
      setSelectedTemplateId(templateId);
    } else if (view === 'edit' && templateId === null) {
      // For new template
      setSelectedTemplateId(null);
    }
  };
  
  // Handle template selection
  const handleSelectTemplate = (id, view = 'edit') => {
    handleChangeView(view, id);
  };
  
  // Handle creating new template
  const handleCreateNew = () => {
    handleChangeView('edit', null);
  };
  
  // Determine page content based on activeView
  const renderContent = () => {
    switch (activeView) {
      case 'edit':
        return (
          <TemplateEditor 
            templateId={selectedTemplateId} 
            onCancel={() => handleChangeView('list')}
            onSave={() => {
              setHasUnsavedChanges(false);
              handleChangeView('list');
            }}
            onChangesDetected={handleChangesDetected}
            onChangesSaved={handleChangesSaved}
          />
        );
      case 'preview':
        return (
          <TemplatePrintPreview 
            templateId={selectedTemplateId}
            onBack={() => handleChangeView('edit', selectedTemplateId)}
          />
        );
      case 'list':
      default:
        return (
          <TemplateList 
            onSelectTemplate={handleSelectTemplate} 
            onCreateNew={handleCreateNew}
          />
        );
    }
  };
  
  // Generate breadcrumb items
  const getBreadcrumbItems = () => {
    const items = [
      <Breadcrumb.Item key="home">
        <HomeOutlined /> Home
      </Breadcrumb.Item>,
      <Breadcrumb.Item 
        key="templates" 
        onClick={() => hasUnsavedChanges ? handleChangeView('list') : setActiveView('list')}
      >
        <FileTextOutlined /> Templates
      </Breadcrumb.Item>
    ];
    
    if (activeView === 'edit') {
      items.push(
        <Breadcrumb.Item key="edit">
          <EditOutlined /> {selectedTemplateId ? 'Edit Template' : 'New Template'}
        </Breadcrumb.Item>
      );
    } else if (activeView === 'preview') {
      items.push(
        <Breadcrumb.Item 
          key="edit" 
          onClick={() => handleChangeView('edit', selectedTemplateId)}
        >
          <EditOutlined /> Edit Template
        </Breadcrumb.Item>
      );
      items.push(
        <Breadcrumb.Item key="preview">
          <PrinterOutlined /> Print Preview
        </Breadcrumb.Item>
      );
    }
    
    return items;
  };
  
  return (
    <Content style={{ padding: '0', minHeight: 'calc(100vh - 64px)' }}>
      
      
      {renderContent()}
    </Content>
  );
};

export default TemplateManagementPage;